export const arrayToCommaSeparatedString = (array: string[]) => {
  return array.reduce((text, name, index) => {
    const isNotLast = index < array.length - 1;
    if (!isNotLast && array.length == 2) {
      return text + ' and ' + name;
    } else if (isNotLast && index !== 0) {
      return text + ', ' + name;
    }
    return text + (index === 0 ? '' : ', and ') + name;
  }, '');
};

export const countWords = (input: string): number => {
  const matches = input.match(/\w+ */g);
  return matches?.length ?? 0;
};

export function equalsIgnoreCase (a: string, b: string) {
  return a.toLowerCase() === b.toLowerCase();
}

export const formatPhoneNumber = (
  phoneNumber: string,
  mask: 'dashes' | 'paren' | 'none' = 'dashes',
) => {
  if (!phoneNumber) {
    return '';
  }
  const phone = phoneNumber.split('#')[0];
  const alias = phoneNumber.split('#')[1];
  const phoneSuffix = alias ? `#${alias}` : '';
  let phoneNum = phone.replace(/[^0-9]+/g, '');
  let formattedPhone = '';
  if (phoneNum.length > 10) {
    formattedPhone += `+${phoneNum.slice(0, -10)} `;
    phoneNum = phoneNum.slice(-10);
  }
  switch (mask) {
    case 'dashes':
      formattedPhone += `${phoneNum.slice(0, 3)}-${phoneNum.slice(3, 6)}-${phoneNum.slice(6)}${phoneSuffix}`;
      break;
    case 'paren':
      formattedPhone += `(${phoneNum.slice(0, 3)}) ${phoneNum.slice(3, 6)}-${phoneNum.slice(6)}${phoneSuffix}`;
      break;
    case 'none':
      formattedPhone += `${phoneNum.toString()}${phoneSuffix}`;
      break;
  }
  return formattedPhone;
};

/**
 * Generate a random token using the characters [A-Za-z0-9].
 * @param length
 */
export function generateRandomToken (length: number = 20): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  while (token.length < length) {
    token += chars[Math.floor(Math.random() * chars.length)];
  }

  return token;
}

export function getFarmerName<T extends {
  firstName: string | null,
  lastName: string | null,
  telephone?: string | null,
}> (
  user: T | undefined,
  useEmail?: boolean,
) {
  let name: string;
  if (user?.firstName && user?.lastName) {
    name = `${user.firstName} ${user.lastName}`.trim();
  } else if (user?.firstName) {
    name = user.firstName;
  } else if (useEmail && user?.telephone) {
    name = user.telephone;
  } else {
    name = '';
  }
  return name.trim();
}

export const getOriginalFileName = (nameString: string) => {
  const lastIndex = nameString?.lastIndexOf('-');
  return nameString?.substring(lastIndex + 1);
};

export function includesIgnoreCase (text: string, searchTerm: string) {
  return text.toLowerCase().includes(searchTerm.toLowerCase());
}

export const isEmptyValue = (value?: number | string | null) => {
  return value == null || (
    typeof value === 'string' ? value.trim() === '' : isNaN(value) || value === 0
  );
};

export const isValidHexColor = (value: string) => {
  return !!value.match(/^#[0-9A-Fa-f]{6}$/);
};

export const keepOnlyDigits = (input: string)=> {
  return input.replace(/\D/g, '');
};

export const stringDistance = (left: string, right: string): number => {
  const lenLeft = left.length;
  const lenRight = right.length;

  // Create a 2D array to store distances
  const dp: number[][] = Array.from({ length: lenLeft + 1 }, () => Array(lenRight + 1).fill(0));

  // Initialize the base cases
  for (let i = 0; i <= lenLeft; i++) dp[i][0] = i; // Deletion
  for (let j = 0; j <= lenRight; j++) dp[0][j] = j; // Insertion

  // Compute distances
  for (let i = 1; i <= lenLeft; i++) {
    for (let j = 1; j <= lenRight; j++) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;

      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,           // Deletion
        dp[i][j - 1] + 1,           // Insertion
        dp[i - 1][j - 1] + cost,    // Substitution
      );
    }
  }

  return dp[lenLeft][lenRight];
};

/**
 * Redact the middle section of private information so you can see leading and trailing characters
 * but not the whole thing.
 */
export function redact (text: string) {
  if (!text.length) { return ""; }
  const tenth = Math.min(Math.ceil(text.length/10), 50);
  return text.substring(0, tenth) + '****' + text.substring(text.length - tenth, text.length);
}

export function removeWhiteSpace (s: string) {
  // Replace any spaces followed by a newline followed by 4 or more spaces with a single space
  return s
    .replace(/^[ \n]*/, '')
    .replace(/[ \n]*$/, '')
    .replace(/[ ]*\n[ ]{4,}/g, ' ')
    .replace(/\n /g, '\n');
}

export const roundToFixed = (num: number, length = 3) => {
  if (isNaN(num)) {
    return '';
  }

  let n = Number(num) * (10 ** length);
  n = Math.round(n);
  n *= (10 ** -length);

  return n.toFixed(length);
};

export const sanitizedFileName = (name: string) => {
  const imageName = name?.substring(0, name.lastIndexOf('.'));
  const extension = name?.substring(name.lastIndexOf('.'));
  const regex = /[^a-zA-Z0-9]/g;
  return `${imageName.replace(regex, '')}${extension}`;
};

export function timeRemainingShort (hours: number) {
  let remaining: number = hours;
  let unit: 's' | 'min' | 'h' = 'h';

  if (hours < 1) {
    remaining = hours * 60;
    unit = 'min';
  }

  if (hours * 60 < 1) {
    remaining = hours * 60 * 60;
    unit = 's';
  }

  if (hours >= 24) {
    let remainingDays = roundToFixed(Math.floor(hours / 24), 0);
    let remainingHours = roundToFixed(hours % 24, 0);

    if (remainingHours === "24") {
      remainingDays = roundToFixed(parseInt(remainingDays) + 1, 0);
      remainingHours = "0";
    }

    return remainingHours !== "0"
      ? `${remainingDays}d${remainingHours}h`
      : `${remainingDays}d`;
  }

  return `${roundToFixed(remaining, 0)}${unit}`;
}

export const titleCaseString = (value: string): string => {
  return value.split(' ')
    .filter(Boolean)
    .map(w => w[0].toUpperCase() + w.substring(1).toLowerCase())
    .join(' ');
};

export const toWordCase = (value: string) => {
  return value
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
};

export const trimValue = (value: string | undefined | null | boolean | unknown) => {
  if(typeof value === 'string') return value.trim();
  return value;
};

export function truncateString (str: string, length: number) {
  if (length >= str.length) { return str; }
  return `${str.slice(0, length)}[truncated]`;
}

export const unformatPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) {
    return "";
  }
  return phoneNumber?.replaceAll(" ", "").replaceAll("(","").replaceAll(")","").replaceAll("-", "");
};
